// CMG simulator entry — mounts CmgSimulatorAssmat from @twg/calculators onto the
// Slim-rendered `#paje-simulator` scaffold (app/views/calculators/simulateur_cmg/_cmg_simulator_vue3.html.slim).
// This entry replaces the legacy Shakapacker pair:
//   - app/javascript/calculators/cmg_simulator_assmat_v3.js (createCmgSimulatorAssmatV3)
//   - app/javascript/packs/cmg_simulator_assmat.js (Turbo lifecycle wrapper)
// Both retired with task 2.6 once the Slim mount tag (task 2.5) was verified.
//
// Note: this entry does NOT import '@twg/ui/styles'. Rails compiles the same
// design-system CSS via Sprockets and serves application.css on every Slim
// page — see docs/dev/14-mobile-app.md §1 "Styling". ESLint enforces the
// no-double-load rule via no-restricted-imports in web-app/eslint.config.js.
//
// Mount-timing model:
//   - ES module scripts (emitted by vite_typescript_tag) are deferred by
//     default, so they execute AFTER document parsing AND typically AFTER
//     Turbo has already fired `turbo:load` on initial page navigation. The
//     legacy Shakapacker pack ran earlier in the cycle and caught the event
//     in time; the Vite entry would miss it.
//   - Solution: mount immediately on first script execution (the DOM is
//     already parsed at this point), and additionally listen for `turbo:load`
//     so subsequent Turbo navigations back to the page re-mount cleanly.
//     `mountSimulator()` is idempotent — the `el.__vueApp__` guard makes
//     double-calls safe.
import { createApp, type App } from 'vue'
import { CmgSimulatorAssmat } from '@twg/calculators'

interface SimulatorElement extends HTMLElement {
  __vueApp__?: App
}

function mountSimulator(): void {
  const el = document.getElementById('paje-simulator') as SimulatorElement | null
  if (!el) return
  if (el.__vueApp__) return // already mounted — defensive against double turbo:load

  const origin = el.dataset.origin || 'single_tool'
  const contractIdAttr = el.dataset.contractId
  const contractId = contractIdAttr && contractIdAttr.length > 0 ? contractIdAttr : null

  const app = createApp(CmgSimulatorAssmat, { origin, contractId })
  app.mount(el)
  el.__vueApp__ = app
}

function unmountSimulator(): void {
  const el = document.getElementById('paje-simulator') as SimulatorElement | null
  if (!el?.__vueApp__) return
  el.__vueApp__.unmount()
  delete el.__vueApp__
}

function handleMaxErrorScroll(event: MouseEvent): void {
  const target = event.target as Element | null
  if (!target?.closest('.cmg-maxerror')) return
  document.getElementById('global-result')?.scrollIntoView()
}

function bootstrapPageBehavior(): void {
  mountSimulator()
  // Scroll-to-results behavior preserved from the legacy pack: clicking a
  // `.cmg-maxerror` notice should bring the global-result section into view.
  // Attached at body level once per page; harmless if the user clicks elsewhere.
  if (document.getElementById('paje-simulator')) {
    document.body.addEventListener('click', handleMaxErrorScroll)
  }
}

// Initial mount on first script execution. The module-script defer guarantees
// the DOM is parsed by now, so `#paje-simulator` (if present on this page) is
// findable.
bootstrapPageBehavior()

// Re-mount when Turbo navigates back to the page (after a cache visit).
document.addEventListener('turbo:load', bootstrapPageBehavior)

document.addEventListener('turbo:before-cache', () => {
  unmountSimulator()
  document.body.removeEventListener('click', handleMaxErrorScroll)
})
